// @ts-nocheck
import { compact } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';

import {
  campaignsHaveAppInstall,
  campaignsHaveDigitalConversions,
  campaignsHaveDriveToStore,
  campaignsHaveViewability,
  getCampaignCreativeFee,
  getItemKeyItemData,
  getPermissions,
  getReportingType,
  hasAudio,
  hasVideo,
  havePricingModel
} from '@/helpers/reporting.helpers';
import {
  getHasLumenAttention,
  getStaticMode,
  includeHouseHoldImpacts,
  someCountryHasHouseHoldImpacts
} from '@/store/selectors/reporting.selectors';
import {
  useAnalyticalCampaignViewByDayStats,
  useAnalyticalViewDayStats,
  useAnalyticalViewHourStats,
  useCampaignDetailStats,
  useSummaryDOOHPatrimonyStats,
  useSummaryGlobalStats
} from '@/ts-resources/reporting/summary.resources';
import SummaryContext, { ProviderState, SUMMARY_ITEM_KEYS, SummaryItemKey } from './SummaryContext';

import type { CampaignProgressAndGoals, PaginatedStatsResult } from 'sonata-static-storage/lib/common/common.types';
import type { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { SummaryItem } from '@/ts-types/reporting.types';

type Props = {
  campaigns: Record<string, any>[];
  children: JSX.Element | JSX.Element[];
  currency: string;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};
export function SummaryProvider({ campaigns, children, currency: initialCurrency, user, userRoles }: Props) {
  const [
    {
      currency,
      summaryAnalyticalCampaignViewByDayStats,
      summaryAnalyticalViewStats,
      summaryDOOHStats,
      summaryDetailStats,
      summaryItemsKeys: allSummaryItemsKeys,
      summaryStats,
      summaryStatsByCampaign
    },
    setState
  ] = useState<ProviderState>({
    currency: initialCurrency,
    summaryAnalyticalCampaignViewByDayStats: {},
    summaryAnalyticalViewStats: {
      lastDays: [],
      lastHours: []
    },
    summaryDOOHStats: {
      screenAvgImpacts: 0,
      screensNumber: 0
    },
    summaryDetailStats: [],
    summaryItemsKeys: [],
    summaryStats: {},
    summaryStatsByCampaign: {}
  });

  const hasLumenAttention = useSelector(getHasLumenAttention);
  const summaryItemsKeys = useMemo(
    () => allSummaryItemsKeys.filter(itemKey => itemKey !== 'lumenAttention' || hasLumenAttention),
    [allSummaryItemsKeys, hasLumenAttention]
  );

  const reportingType: CampaignFamilyEnum | 'both' = getReportingType(campaigns);
  const isAnAdopsUser = user.userRole.toLowerCase() === 'adops';
  const canAvoidDataFees = user.billing.method !== 'prepaid';
  const staticMode = useSelector(getStaticMode);
  const hasHouseHoldImpactsEnabled = useSelector(includeHouseHoldImpacts);
  const hasSomeCountryWithHouseHoldImpacts = useSelector(someCountryHasHouseHoldImpacts);
  const lumenAttentionPermission = checkUserResourcePermissions(
    'reportingMediaQualityAttentionMetrics',
    user,
    userRoles
  );
  const analyticalViewPermission =
    checkUserResourcePermissions('reportingShowAnalyticalViewSummaryBoxes', user, userRoles) && !staticMode;
  const detailStatsPermission = checkUserResourcePermissions('reportingCampaignItemDetails', user, userRoles);

  const tokens: Record<string, any> = {
    analyticalCampaignStatsByDay: {},
    analyticalStatsByDay: {},
    analyticalStatsByHour: {},
    detailStats: {},
    doohStats: {},
    legacyAttributionStats: {},
    stats: {}
  };

  useEffect(() => {
    return () => {
      tokens.analyticalCampaignStatsByDay?.cancel?.();
      tokens.analyticalStatsByDay?.cancel?.();
      tokens.analyticalStatsByHour?.cancel?.();
      tokens.detailStats?.cancel?.();
      tokens.doohStats?.cancel?.();
      tokens.legacyAttributionStats?.cancel?.();
      tokens.stats?.cancel?.();
    };
  }, []);

  const cids = campaigns.map(({ id }) => id as string);
  const legacyAttributionCids = campaigns
    .filter(campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore)
    .map(({ id }) => id as string);
  const isMixedAttribution = campaignsHaveDriveToStore(campaigns) && legacyAttributionCids.length > 0;
  const [summaryLegacyAttributionStats, setSummaryLegacyAttributionStats] = useState<Record<string, any>>({});

  const getSummaryStats = () => {
    return summaryStats;
  };

  const getSummaryStatsByCampaign = () => {
    return summaryStatsByCampaign;
  };

  const getSummaryDOOHStats = () => {
    return summaryDOOHStats;
  };

  const getSummaryAnalyticalStats = () => {
    return summaryAnalyticalViewStats;
  };

  const getSummaryDetailStats = () => {
    return summaryDetailStats;
  };

  const getSummaryAnalyticalCampaignByDayStats = () => {
    return summaryAnalyticalCampaignViewByDayStats;
  };

  const updateSummaryDOOHStatsState = (result: PaginatedStatsResult) => {
    const getSummaryDOOHByCampaign = (results: Record<string, any>[]) => {
      return campaigns.reduce(
        (acc, { id }) => {
          const campaignsResults = results.filter(({ cidId }) => cidId === id);
          const totalPlayOuts = campaignsResults.reduce((acc2, { playouts }) => {
            return acc2 + (playouts || 0);
          }, 0);
          const screensNumber = [...new Set(campaignsResults.map(({ screenIdId }) => screenIdId))].length;
          return {
            screenAvgImpactsByCampaign: {
              ...acc.screenAvgImpactsByCampaign,
              [id]: totalPlayOuts / screensNumber
            },
            screensNumberByCampaign: {
              ...acc.screensNumberByCampaign,
              [id]: screensNumber
            }
          };
        },
        {
          screenAvgImpactsByCampaign: {},
          screensNumberByCampaign: {}
        }
      );
    };
    const totalScreens =
      result.metadata.groupOptions.find(({ groupField }) => groupField === 'screenId')?.totalSize || 0;
    setState(prevState => ({
      ...prevState,
      summaryDOOHStats: {
        screenAvgImpacts: result.metadata.totalResults.playouts / totalScreens,
        screensNumber: totalScreens,
        // @ts-ignore
        ...getSummaryDOOHByCampaign(result.results)
      }
    }));
  };

  const updateAnalyticalViewDayStatsState = (result: PaginatedStatsResult) => {
    setState(prevState => ({
      ...prevState,
      summaryAnalyticalViewStats: {
        ...prevState.summaryAnalyticalViewStats,
        lastDays: result.results
      }
    }));
  };

  const updateAnalyticalViewHoursStatsState = (result: PaginatedStatsResult) => {
    setState(prevState => ({
      ...prevState,
      summaryAnalyticalViewStats: {
        ...prevState.summaryAnalyticalViewStats,
        lastHours: result.results
      }
    }));
  };

  const updateAnalyticalCampaignViewByDayStatsState = (result: PaginatedStatsResult, cid: string) => {
    setState(prevState => ({
      ...prevState,
      summaryAnalyticalCampaignViewByDayStats: {
        ...prevState.summaryAnalyticalCampaignViewByDayStats,
        [cid]: result.results.reverse()
      }
    }));
  };

  const updateDetailStatsState = (result: CampaignProgressAndGoals[]) => {
    setState(prevState => ({
      ...prevState,
      summaryDetailStats: result
    }));
  };

  const updateLegacyAttributionStatsState = (result: PaginatedStatsResult) => {
    setSummaryLegacyAttributionStats(result.metadata.totalResults);
  };

  const updateSummaryStatsState = (result: PaginatedStatsResult) => {
    const stats = result.metadata.totalResults;
    const statsByCampaign = result.results.reduce(
      (acc, { cidId, ...cidStats }) => ({
        ...acc,
        [cidId]: cidStats
      }),
      {}
    );

    const {
      appsInstallsPermission,
      avgFootfallAttributionPermission,
      digitalConversionPermission,
      doohPermission,
      footfallAttributionPermission,
      mediaFeePermission,
      reportingGTotal,
      viewabilityPermission
    } = getPermissions(user, userRoles, stats);

    const isDigital = isDigitalCampaign(reportingType);
    const isDOOH = isDOOHCampaign(reportingType);
    const hasViewability = !!(campaignsHaveViewability(campaigns, stats) && viewabilityPermission && !isDOOH);
    const hasAttribution =
      campaigns.some(campaign => campaign.hasNewAttributionStats && !campaign.hasDriveToStore) &&
      (avgFootfallAttributionPermission || footfallAttributionPermission);
    const hasDriveToStoreAttribution =
      campaignsHaveDriveToStore(campaigns) && (avgFootfallAttributionPermission || footfallAttributionPermission);
    const hasAppsInstall = campaignsHaveAppInstall(stats) && !isDOOH && appsInstallsPermission;
    const hasDigitalConversions = digitalConversionPermission && !!campaignsHaveDigitalConversions(campaigns);
    const showGTotal = !isAnAdopsUser && canAvoidDataFees && reportingGTotal;

    const itemsConditions: Record<SummaryItemKey, boolean> = {
      appsInstall: hasAppsInstall && !isDOOH && appsInstallsPermission,
      attribution: hasAttribution,
      audio: hasAudio(stats, campaigns),
      clicks: !isDOOH,
      digitalConversions: hasDigitalConversions && !isDOOH && digitalConversionPermission,
      doohPatrimony: !isDigital && doohPermission,
      driveToStoreAttribution: hasDriveToStoreAttribution,
      engagements: !isDOOH && havePricingModel(campaigns, PricingModelEnum.ppe),
      impressions: true,
      lumenAttention: !isDOOH && lumenAttentionPermission,
      pageViewed: !isDOOH && havePricingModel(campaigns, PricingModelEnum.pppv),
      spend: mediaFeePermission || showGTotal,
      video: hasVideo(stats, campaigns),
      viewability: hasViewability && viewabilityPermission && !isDOOH
    };

    setState(prevState => ({
      ...prevState,
      summaryItemsKeys: SUMMARY_ITEM_KEYS.filter(summaryItemKey => itemsConditions[summaryItemKey]),
      summaryStats: stats,
      summaryStatsByCampaign: statsByCampaign
    }));
  };

  const { isLoading: statsAreLoading, status: statsStatus } = useSummaryGlobalStats(
    user.id!,
    cids,
    currency,
    tokens.stats,
    updateSummaryStatsState
  );

  const { isLoading: legacyAttributionStatsAreLoading } = useSummaryGlobalStats(
    user.id!,
    legacyAttributionCids,
    currency,
    tokens.legacyAttributionStats,
    updateLegacyAttributionStatsState,
    isMixedAttribution
  );

  const {
    isFetching: doohStatsAreFetching,
    isLoading: doohStatsAreLoading,
    status: doohStatsStatus
  } = useSummaryDOOHPatrimonyStats(
    user.id!,
    cids,
    currency,
    summaryItemsKeys,
    tokens.doohStats,
    updateSummaryDOOHStatsState
  );

  const {
    isFetching: lastDaysStatsAreFetching,
    isLoading: lastDaysStatsAreLoading,
    status: lastDaysStatsStatus
  } = useAnalyticalViewDayStats(
    user.id!,
    cids,
    currency,
    analyticalViewPermission,
    tokens.analyticalStatsByDay,
    updateAnalyticalViewDayStatsState
  );

  const {
    isFetching: lastHoursStatsAreFetching,
    isLoading: lastHoursStatsAreLoading,
    status: lastHoursStatsStatus
  } = useAnalyticalViewHourStats(
    user.id!,
    cids,
    currency,
    analyticalViewPermission,
    tokens.analyticalStatsByHour,
    updateAnalyticalViewHoursStatsState
  );

  const {
    isFetching: detailStatsAreFetching,
    isLoading: detailStatsAreLoading,
    refetch: summaryDetailStatsReFetch,
    status: detailStatsStatus
  } = useCampaignDetailStats(user.id!, cids, tokens.detailStats, detailStatsPermission, updateDetailStatsState);

  const {
    isFetching: analyticalStatsAreFetching,
    isLoading: analyticalStatsAreLoading,
    statuses: analyticalStatsStatuses
  } = useAnalyticalCampaignViewByDayStats(
    user.id!,
    cids,
    currency,
    tokens.analyticalCampaignStatsByDay,
    updateAnalyticalCampaignViewByDayStatsState
  );

  const getSummaryItem = (
    itemKey: SummaryItemKey,
    campaignsData: Record<string, any>[],
    stats: Record<string, any> = {}
  ): SummaryItem => {
    const totalCreativeFee = getCampaignCreativeFee(campaignsData);
    const { reportingGTotal } = getPermissions(user, userRoles, summaryStats);
    const scopedStats =
      itemKey === 'attribution' && isMixedAttribution && campaignsData.length > 1
        ? summaryLegacyAttributionStats
        : stats;

    return getItemKeyItemData(
      summaryAnalyticalViewStats,
      campaignsData,
      reportingType,
      totalCreativeFee,
      scopedStats,
      {
        countriesIncludeHouseHoldImpactsEvents: hasHouseHoldImpactsEnabled,
        showGTotal: !isAnAdopsUser && canAvoidDataFees && reportingGTotal,
        someCountryIncludesHouseHoldImpactsEvents: hasSomeCountryWithHouseHoldImpacts
      },
      user,
      userRoles,
      itemKey
    ) as SummaryItem;
  };

  const getSummaryItems = (
    itemKeys: SummaryItemKey[],
    campaignsData: Record<string, any>[],
    stats: Record<string, any> = {}
  ): SummaryItem[] => {
    return compact(itemKeys.map(summaryItemKey => getSummaryItem(summaryItemKey, campaignsData, stats)));
  };

  const analyticalStatsReady =
    !analyticalViewPermission ||
    (!lastDaysStatsAreFetching && !lastDaysStatsAreLoading && !lastHoursStatsAreLoading && !lastHoursStatsAreFetching);

  const DOOHStatsReady = !summaryItemsKeys.includes('doohPatrimony') || !doohStatsAreLoading || !doohStatsAreFetching;
  const summaryDetailStatsReady = !detailStatsAreFetching && !detailStatsAreLoading;
  const summaryAnalyticalCampaignByDayStatsError = analyticalStatsStatuses.some(status => status === 'error');
  const summaryAnalyticalCampaignByDayStatsReady = !analyticalStatsAreFetching && !analyticalStatsAreLoading;
  const summaryAnalyticalStatsError = lastDaysStatsStatus === 'error' || lastHoursStatsStatus === 'error';
  const summaryAnalyticalStatsReady = analyticalStatsReady;
  const summaryDetailStatsError = detailStatsStatus === 'error';
  const summaryStatsError = statsStatus === 'error' || doohStatsStatus === 'error';
  const summaryStatsReady = !statsAreLoading && !legacyAttributionStatsAreLoading && DOOHStatsReady;

  const value = useMemo(
    () => ({
      getSummaryAnalyticalCampaignByDayStats,
      getSummaryAnalyticalStats,
      getSummaryDOOHStats,
      getSummaryDetailStats,
      getSummaryItem,
      getSummaryItems,
      getSummaryStats,
      getSummaryStatsByCampaign,
      summaryAnalyticalCampaignByDayStatsError,
      summaryAnalyticalCampaignByDayStatsReady,
      summaryAnalyticalStatsError,
      summaryAnalyticalStatsReady,
      summaryDetailStatsError,
      summaryDetailStatsReFetch,
      summaryDetailStatsReady,
      summaryItemsKeys,
      summaryStatsError,
      summaryStatsReady
    }),
    [
      getSummaryAnalyticalCampaignByDayStats,
      getSummaryAnalyticalStats,
      getSummaryDOOHStats,
      getSummaryDetailStats,
      getSummaryItem,
      getSummaryItems,
      getSummaryStats,
      getSummaryStatsByCampaign,
      summaryAnalyticalCampaignByDayStatsError,
      summaryAnalyticalCampaignByDayStatsReady,
      summaryAnalyticalStatsError,
      summaryAnalyticalStatsReady,
      summaryDetailStatsError,
      summaryDetailStatsReFetch,
      summaryDetailStatsReady,
      summaryItemsKeys,
      summaryStatsError,
      summaryStatsReady
    ]
  );

  return <SummaryContext.Provider value={value}>{children}</SummaryContext.Provider>;
}
